import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { IconPlus } from "@tabler/icons-react";
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { DialogTrigger } from "react-aria-components";
import { PageBody, PageLayout } from "../components/layout/pageLayout.js";
import { selectCompany } from "../features/company/companySlice.js";
import { useDeepLinkedProposal } from "../features/copilot/copilotDeepLinks.js";
import {
  NotificationType,
  showNotification,
} from "../features/notificationsOverlay/notificationsSlice.js";
import { ProjectsTable } from "../features/projects/projectsTable.js";
import { CreateTrainingModal } from "../features/projects/projectTemplates/createTrainingModal.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { Can } from "../helpers/teammateAuthorizationContext.js";
import { useAppDispatch, useAppSelector } from "../hooks/hook.js";

type CreateTrainingProposal = NonNullable<
  ComponentProps<typeof CreateTrainingModal>["proposal"]
>;

export function TrainingsPage(): ReactNode {
  const company = useAppSelector(selectCompany);
  const teammate = useAppSelector(selectTeammate);
  // The trigger button opens the dialog on its own; this is here for the deep
  // link below, which opens it without a press.
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // An Ask Alpharun card proposing a new training links here (?proposalId=):
  // the training has no project yet, so the proposal opens the create modal
  // pre-filled and the builder replays its operations after creation.
  const [proposal, setProposal] = useState<CreateTrainingProposal | undefined>(
    undefined,
  );
  const dispatch = useAppDispatch();
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("training", {
      onUnavailable: () =>
        showNotification(dispatch, {
          id: `training-proposal-unavailable-${Date.now().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "This suggestion is no longer available.",
        }),
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (deepLinkProposal.data.kind !== "create_training") {
      return;
    }
    setProposal({
      id: deepLinkProposal.id,
      name: deepLinkProposal.data.name,
      playbookId: deepLinkProposal.data.playbook_id,
    });
    setIsCreateModalOpen(true);
  }, [deepLinkProposal, clearDeepLinkProposalId]);

  if (!company || !teammate) return <></>;

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title="Trainings"
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.JOB_OPENINGS}
            passThrough={true}
          >
            {(allowed) => (
              <DialogTrigger
                isOpen={isCreateModalOpen}
                onOpenChange={(isOpen) => {
                  setIsCreateModalOpen(isOpen);
                  if (!isOpen) {
                    setProposal(undefined);
                  }
                }}
              >
                <Button
                  size={Size.SMALL}
                  variant={ButtonVariant.Primary}
                  icon={<IconPlus className="h-4 w-4" />}
                  isDisabled={!allowed}
                >
                  Create training
                </Button>
                <CreateTrainingModal proposal={proposal} />
              </DialogTrigger>
            )}
          </Can>
        }
      />

      <PageBody width={PageWidth.Standard}>
        <ProjectsTable filterType="training" />
      </PageBody>
    </PageLayout>
  );
}
